"use client"

import {type ReactNode} from "react"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {cn} from "@/lib/utils"

export function DetailFlyout({
  open,
  onClose,
  title,
  meta,
  icon,
  children,
  ariaLabel,
  variant = "default",
}: {
  open: boolean
  onClose: () => void
  title: string
  meta?: ReactNode
  icon?: ReactNode
  children: ReactNode
  ariaLabel?: string
  variant?: "default" | "changelog"
}) {
  const isChangelog = variant === "changelog"

  return (
    <Sheet open={open} onOpenChange={nextOpen => {
      if (!nextOpen) onClose()
    }}>
      <SheetContent
        side="right"
        aria-label={ariaLabel}
        overlayClassName={isChangelog ? "bg-[rgba(15,18,28,0.34)] duration-150" : undefined}
        className={cn(
          "gap-0 p-0 shadow-[-20px_0_50px_-20px_rgb(10_10_11_/_0.22)]",
          isChangelog
            ? "w-[min(480px,94vw)] data-[state=closed]:duration-150 data-[state=open]:duration-200 sm:max-w-[480px] [&_[data-slot=sheet-close]]:right-[22px] [&_[data-slot=sheet-close]]:top-[18px] [&_[data-slot=sheet-close]]:flex [&_[data-slot=sheet-close]]:size-[30px] [&_[data-slot=sheet-close]]:items-center [&_[data-slot=sheet-close]]:justify-center [&_[data-slot=sheet-close]]:rounded-md [&_[data-slot=sheet-close]]:border [&_[data-slot=sheet-close]]:bg-card [&_[data-slot=sheet-close]]:opacity-100 [&_[data-slot=sheet-close]]:shadow-xs [&_[data-slot=sheet-close]]:data-[state=open]:bg-card"
            : "w-[min(520px,92vw)] sm:max-w-[520px]",
        )}
      >
        <SheetHeader className={cn(
          "border-b pr-12",
          isChangelog ? "min-h-[94px] flex-row items-start gap-3 px-[22px] pb-4 pt-[18px]" : "flex-row items-center gap-3 px-[18px] py-3.5",
        )}>
          {icon && (
            <div className="flex size-8 shrink-0 items-center justify-center">
              {icon}
            </div>
          )}
          <div className="min-w-0 flex-1">
            {isChangelog && meta && (
              <SheetDescription asChild>
                <div className="mb-2 flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{meta}</div>
              </SheetDescription>
            )}
            <SheetTitle className={cn(
              isChangelog ? "text-xl font-medium leading-tight tracking-[-0.02em]" : "truncate text-[13.5px] font-semibold tracking-[-0.005em]",
            )}>{title}</SheetTitle>
            {!isChangelog && meta && (
              <SheetDescription asChild>
                <div className="mt-0.5 text-[11px] text-muted-foreground">{meta}</div>
              </SheetDescription>
            )}
          </div>
        </SheetHeader>

        <div className={cn(
          "flex-1 overflow-y-auto",
          isChangelog ? "px-[22px] pb-8 pt-5" : "space-y-[18px] p-[18px]",
        )}>
          {children}
        </div>
      </SheetContent>
    </Sheet>
  )
}
